import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { quickLogUrl } from '@goaltree/backend/convex/lib/dayShare'
import { qrMatrix, qrPath } from '@goaltree/backend/convex/lib/qr'
import { useMutation, useQuery } from 'convex/react'
import { ChevronDownIcon, CopyIcon, QrCodeIcon, RotateCcwIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

export function QrCode({ text, className }: { text: string; className?: string }) {
  const m = useMemo(() => qrMatrix(text), [text])
  if (!m) return null
  const n = m.length + 4
  return (
    <svg viewBox={`0 0 ${n} ${n}`} className={className} shapeRendering="crispEdges" role="img">
      <rect width={n} height={n} fill="#fff" />
      <path d={qrPath(m, 2)} fill="#000" />
    </svg>
  )
}

// Habit detail: a secret gt.today/+token link that logs this habit in one tap.
export function QuickLogLinkRow({
  habitId,
  isNumber,
}: {
  habitId: Id<'nodes'>
  isNumber: boolean
}) {
  const { t } = useTranslation()
  const link = useQuery(api.quickLinks.getForHabit, { habitId })
  const ensure = useMutation(api.quickLinks.ensure)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const url = link ? quickLogUrl(link.token) : null

  async function create(reset = false) {
    if (reset && !window.confirm(t('quickLog.resetConfirm'))) return
    setBusy(true)
    try {
      await ensure({ habitId, reset })
      setOpen(true)
      if (reset) toast.success(t('quickLog.resetDone'))
    } catch {
      toast.error(t('common.error'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-border/60 px-3 py-2.5">
      <div className="flex items-start gap-2.5">
        <QrCodeIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-foreground">{t('quickLog.title')}</p>
          <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
            {t('quickLog.desc')}
          </p>
        </div>
        {link === undefined ? null : url ? (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            className="mt-0.5 flex shrink-0 cursor-pointer items-center gap-1 text-xs font-semibold text-primary-text hover:underline"
          >
            {t('quickLog.show')}
            <ChevronDownIcon
              className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
            />
          </button>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={() => void create()}
            className="mt-0.5 shrink-0 cursor-pointer whitespace-nowrap rounded-full border border-primary/40 px-2.5 py-1 text-xs font-semibold text-primary-text transition-colors hover:bg-primary/10 disabled:opacity-50"
          >
            {t('quickLog.create')}
          </button>
        )}
      </div>
      {url && open && (
        <div className="flex flex-col items-center gap-2.5 pb-1">
          <QrCode text={url} className="h-40 w-40 rounded-lg" />
          <code className="max-w-full truncate rounded-md bg-muted px-2 py-1 text-[11px]">
            {url}
          </code>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() =>
                void navigator.clipboard
                  .writeText(url)
                  .then(() => toast.success(t('common.copied')))
              }
              className="flex h-8 cursor-pointer items-center gap-1.5 rounded-lg bg-primary px-3 text-xs font-bold text-primary-foreground hover:bg-primary/90"
            >
              <CopyIcon className="h-3.5 w-3.5" />
              {t('quickLog.copy')}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void create(true)}
              className="flex h-8 cursor-pointer items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              <RotateCcwIcon className="h-3.5 w-3.5" />
              {t('quickLog.reset')}
            </button>
          </div>
          <p className="text-center text-[11px] text-muted-foreground">
            {t(isNumber ? 'quickLog.hintNumber' : 'quickLog.hintCheck')} {t('quickLog.hintGo')}
          </p>
        </div>
      )}
    </div>
  )
}
